import React, { useState, useEffect } from 'react';
import { donationService } from '../services/api';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';

interface GroceryItem {
  id: string;
  name: string;
  estimatedPrice: number;
  icon: string;
  isActive: boolean;
  displayOrder: number;
}

const ManageGroceryItems: React.FC = () => {
  const [items, setItems] = useState<GroceryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<GroceryItem | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState<GroceryItem>({
    id: '',
    name: '',
    estimatedPrice: 0,
    icon: '🛒',
    isActive: true,
    displayOrder: 0,
  });

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await donationService.getGroceryItems();
      setItems(data);
    } catch (error) {
      console.error('Error loading items:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load grocery items',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      id: 'auto',
      name: '',
      estimatedPrice: 0,
      icon: '🛒',
      isActive: true,
      displayOrder: items.length + 1,
    });
    setShowModal(true);
  };

  const handleEdit = (item: GroceryItem) => {
    setEditingItem(item);
    setFormData({
      id: item.id,
      name: item.name,
      estimatedPrice: item.estimatedPrice,
      icon: item.icon,
      isActive: item.isActive,
      displayOrder: item.displayOrder,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const payload = {
        itemId: formData.id,
        itemName: formData.name,
        estimatedPrice: formData.estimatedPrice,
        icon: formData.icon,
        isActive: formData.isActive,
        displayOrder: formData.displayOrder,
      };

      if (editingItem) {
        await donationService.updateGroceryItem(payload);
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Grocery item updated successfully',
          timer: 2000,
        });
      } else {
        await donationService.addGroceryItem(payload);
        Swal.fire({
          icon: 'success',
          title: 'Added!',
          text: 'Grocery item added successfully',
          timer: 2000,
        });
      }

      setShowModal(false);
      loadItems();
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.message || 'Failed to save grocery item',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this grocery item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await donationService.deleteGroceryItem(itemId);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Grocery item deleted successfully',
          timer: 2000,
        });
        loadItems();
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.message || 'Failed to delete grocery item',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const commonEmojis = ['🍚', '🌾', '🫘', '🛢️', '🧈', '🍬', '🧂', '🌶️', '🥛', '🧀', '🥬', '🥕', '🍅', '🥔', '🧅', '🧄', '🫑', '🥒', '🍞', '🥐'];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #f0f9ff 100%)' }} className="py-4">
      {/* Header */}
      <div className="gradient-header">
        <div className="container">
          <h1 className="h3 fw-bold mb-1">
            <i className="fas fa-cog me-2"></i>Manage Grocery Items
          </h1>
          <p className="small mb-0">Add, edit, or remove grocery items</p>
        </div>
      </div>

      <style>{`
        .gradient-header {
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #38bdf8 100%);
          color: white;
          padding: 1.5rem 1rem;
          text-align: center;
          box-shadow: 0 8px 25px rgba(251, 191, 36, 0.3);
          margin-bottom: 2rem;
        }

        .item-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          margin-bottom: 1rem;
        }

        .item-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .btn-action {
          margin: 0 0.25rem;
        }

        .modal-content {
          border-radius: 15px;
        }

        .emoji-picker {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .emoji-option {
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.5rem;
          border: 2px solid transparent;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .emoji-option:hover {
          background: #f0f9ff;
          border-color: #38bdf8;
        }

        .emoji-option.selected {
          background: #dbeafe;
          border-color: #3b82f6;
        }
      `}</style>

      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Grocery Items ({items.length})</h4>
          <button className="btn btn-primary btn-lg" onClick={handleAdd} disabled={loading}>
            <i className="fas fa-plus me-2"></i>Add New Item
          </button>
        </div>

        {loading && items.length === 0 ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row">
            {items.map((item) => (
              <div key={item.id} className="col-md-6 col-lg-4">
                <div className="item-card">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div style={{ fontSize: '2.5rem' }}>{item.icon}</div>
                    <div>
                      <button
                        className="btn btn-sm btn-outline-primary btn-action"
                        onClick={() => handleEdit(item)}
                        disabled={loading}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger btn-action"
                        onClick={() => handleDelete(item.id)}
                        disabled={loading}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                  <h5 className="fw-bold mb-2">{item.name}</h5>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-success fw-bold">${item.estimatedPrice.toFixed(2)}</span>
                    <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <small className="text-muted">Display Order: {item.displayOrder}</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingItem ? 'Edit Grocery Item' : 'Add New Grocery Item'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {editingItem && (
                    <div className="mb-3">
                      <label className="form-label fw-bold">Item ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.id}
                        disabled
                        readOnly
                      />
                      <small className="text-muted">Item ID cannot be changed</small>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-bold">Item Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="e.g., Rice (10 kg)"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Estimated Price ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={formData.estimatedPrice}
                      onChange={(e) => setFormData({ ...formData, estimatedPrice: parseFloat(e.target.value) })}
                      required
                      min="0"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Icon *</label>
                    <div className="emoji-picker">
                      {commonEmojis.map((emoji) => (
                        <span
                          key={emoji}
                          className={`emoji-option ${formData.icon === emoji ? 'selected' : ''}`}
                          onClick={() => setFormData({ ...formData, icon: emoji })}
                        >
                          {emoji}
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      className="form-control mt-2"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="Or enter custom emoji"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Display Order *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                      required
                      min="1"
                    />
                  </div>

                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Active (visible to users)
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {editingItem ? 'Update' : 'Add'} Item
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageGroceryItems;
